import React, { useEffect, useState } from 'react';
import {
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { useLocale } from '../i18n/LocaleContext';

type Props = {
  /** Сколько записей ещё не читали. Ноль — лампочка ровная и приглушённая. */
  unreadCount: number;
  onPress: () => void;
  /** Цвет штриха — тот же, что у остальных иконок шапки (`chatListHeaderIconTint`). */
  tint: string;
  /** Оформление кнопки из стилей списка чатов: круг, фон, рамка. */
  buttonStyle?: StyleProp<ViewStyle>;
  badgeStyle?: StyleProp<ViewStyle>;
  badgeTextStyle?: StyleProp<TextStyle>;
};

/** Полный цикл мигания. Медленно и намеренно: это приглашение, а не тревога. */
const BLINK_PERIOD_MS = 1600;

/** Насколько лампочка гаснет в нижней точке. Ноль читался бы как «иконка пропала». */
const BLINK_MIN_OPACITY = 0.35;

/**
 * Лампочка «Подсказок и новостей» в шапке списка чатов (заказ владельца 2026-08-31).
 *
 * <p><b>Почему мигает, а не просто светится.</b> Ровную иконку в ряду из трёх других не
 * замечают: она выглядит четвёртой кнопкой, которая была тут всегда. Мигание — единственное,
 * что глаз ловит на периферии, и ровно поэтому оно должно быть медленным и неглубоким: частое
 * или до нуля читается как ошибка, а не как «загляни сюда».
 *
 * <p><b>Почему мигание кончается само.</b> Оно привязано к непрочитанному, а прочитанным
 * запись становится в момент открытия ящика. Значит, вечно мигающей лампочки не бывает: один
 * заход её гасит, и следующий раз она мигнёт только из-за действительно новой записи.
 *
 * <p>Число непрочитанного — тем же значком, что и заявки в контакты: два разных способа сказать
 * «здесь N штук» в одной шапке человек читал бы как два разных смысла.
 */
export function HelpBulbButton({
  unreadCount,
  onPress,
  tint,
  buttonStyle,
  badgeStyle,
  badgeTextStyle,
}: Props): React.ReactElement {
  const { t } = useLocale();
  const [blink] = useState(() => new Animated.Value(1));
  const blinking = unreadCount > 0;

  useEffect(() => {
    if (!blinking) {
      // Останавливаем и возвращаем в полную яркость: брошенная на полпути анимация оставила бы
      // прочитанную лампочку навсегда полупрозрачной.
      blink.stopAnimation();
      blink.setValue(1);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(blink, {
          toValue: BLINK_MIN_OPACITY,
          duration: BLINK_PERIOD_MS / 2,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(blink, {
          toValue: 1,
          duration: BLINK_PERIOD_MS / 2,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      blink.setValue(1);
    };
  }, [blink, blinking]);

  return (
    <Pressable
      onPress={onPress}
      hitSlop={8}
      style={buttonStyle}
      accessibilityRole="button"
      accessibilityLabel={blinking ? t('helpCenterOpenLabelUnread') : t('helpCenterOpenLabel')}>
      <Animated.View style={{ opacity: blink }}>
        <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
          {/* Колба и цоколь: тот же штрих 1.8, что у иконок меню сообщения. */}
          <Path
            d="M12 3a6 6 0 0 0-3.6 10.8c.6.45.95 1.05 1.03 1.7l.07.5h5l.07-.5c.08-.65.43-1.25 1.03-1.7A6 6 0 0 0 12 3z"
            stroke={tint}
            strokeWidth={1.8}
            strokeLinejoin="round"
          />
          <Path d="M9.7 17.6h4.6" stroke={tint} strokeWidth={1.8} strokeLinecap="round" />
          <Path d="M10.6 20h2.8" stroke={tint} strokeWidth={1.8} strokeLinecap="round" />
        </Svg>
      </Animated.View>
      {unreadCount > 0 ? (
        <View style={[styles.badgeFallback, badgeStyle]}>
          <Text style={badgeTextStyle}>{unreadCount > 9 ? '9+' : String(unreadCount)}</Text>
        </View>
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  /**
   * Запасная геометрия значка на случай, если стили списка чатов его не передали: без неё
   * число легло бы поверх колбы и не читалось.
   */
  badgeFallback: { position: 'absolute', top: -4, right: -6 },
});
